import { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { DEV_ACCOUNTS } from '../constants';
import { isDevEnv } from '../firebase';
import Button from './ui/Button';

export default function Login() {
  const { signIn, signInDev } = useAuth();
  const [loading, setLoading] = useState(null);

  const showDevAccounts = isDevEnv;

  const handleOkta = async () => {
    setLoading('okta');
    try { await signIn(); } catch { /* handled by Firebase */ }
    setLoading(null);
  };

  const handleDev = async (email) => {
    setLoading(email);
    try { await signInDev(email); } catch (e) { console.error(e); }
    setLoading(null);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel: gradient */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-navy-900 via-accent-900 to-navy-950">
        {/* Decorative shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-accent-500/20 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-1/3 right-1/3 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        </div>
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center mb-8">
              <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-display-lg text-white mb-4">
              Comp<br />Companion
            </h1>
            <p className="text-lg text-white/60 max-w-sm leading-relaxed">
              Review and present compensation packages with clarity and confidence.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right panel: login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-surface-50">
        <motion.div
          className="w-full max-w-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-500 to-cyan-500 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="text-xl font-bold text-navy-900">Comp Companion</span>
          </div>

          <h2 className="text-2xl font-bold text-navy-900 mb-2">Welcome back</h2>
          <p className="text-navy-400 mb-8">Sign in to access your compensation details.</p>

          {/* Sign-in button */}
          <Button onClick={handleOkta} loading={loading === 'okta'} size="lg" className="w-full mb-6">
            Sign in
          </Button>

          {/* Dev accounts (dev environment only) */}
          {showDevAccounts && (
            <>
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-surface-300" /></div>
                <div className="relative flex justify-center text-xs font-medium text-navy-400 uppercase tracking-wider">
                  <span className="bg-surface-50 px-3">Development</span>
                </div>
              </div>

              <div className="space-y-2.5">
                {DEV_ACCOUNTS.map((acc) => (
                  <button
                    key={acc.email}
                    onClick={() => handleDev(acc.email)}
                    disabled={!!loading}
                    className="w-full flex items-center gap-4 px-4 py-3 rounded-xl border border-surface-200 bg-white hover:border-accent-300 hover:shadow-card transition-all duration-200 group text-left"
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-accent-100 to-accent-200 flex items-center justify-center text-accent-700 text-xs font-bold shrink-0 group-hover:scale-105 transition-transform">
                      {acc.label[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-navy-800 truncate">{acc.label}</p>
                      <p className="text-xs text-navy-400 truncate">{acc.description}</p>
                    </div>
                    {loading === acc.email && (
                      <svg className="animate-spin h-4 w-4 text-accent-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
